import { useMemo, useRef } from "react";
import DataTable, { type Cell, SearchBar } from "./ServerSideTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useCenter, useCenterUsersPaginated } from "../../hooks/centers.hooks";
import i18n from "../../i18n";
import {
	Avatar,
	Box,
	Flex,
	Modal,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	useDisclosure,
	Text,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	Button,
	type MenuProps,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
} from "@chakra-ui/react";
import PrimaryButton from "../PrimaryButton";
import { AddIcon } from "@chakra-ui/icons";
import AttachedFileIcon from "../icons/AttachedFileIcon";
import LinkUserForm, {
	type InviteUserFormHandler,
} from "../forms/LinkUserForm";
import { sendInvitations } from "../../mutations/center.mutations";
import { useMutation, useQueryClient } from "react-query";
import type { User } from "../../types/user.types";
import { useTryLaterToast } from "../../hooks/toast.hooks";
import {
	deleteUser,
	unlinkUser,
	updateUser,
} from "../../mutations/user.mutations";
import MoreIcon from "../icons/MoreIcon";
import UserForm from "../forms/UserForm";
import type { Center } from "../../types/center.types";
import SimpleModal from "../modals/SimpleModal";
import UserCenterForm from "../forms/UserCenterForm";

function MoreMenu({
	center,
	user,
	...props
}: { center?: Center | null; user: User } & Omit<MenuProps, "children">) {
	const queryClient = useQueryClient();
	const { tryLater } = useTryLaterToast();
	const { mutate: mutateUpdateUser } = useMutation(updateUser, {
		onSuccess: () => {
			queryClient.invalidateQueries("center-users-paginated");
		},
	});
	const { mutate: mutateDeleteUser } = useMutation(deleteUser, {
		onSuccess: () => {
			queryClient.invalidateQueries("center-users-paginated");
		},
	});
	const { mutate: mutateUnlinkUser } = useMutation(unlinkUser, {
		onSuccess: () => {
			queryClient.invalidateQueries("center-users-paginated");
		},
	});
	const handleGiveAdminAccessClick = () => {
		if (!center) {
			return;
		}
		mutateUpdateUser(
			{ id: user.id, centerId: center.id, access: "ADMIN" },
			{
				onError: () => {
					tryLater();
				},
			},
		);
	};
	const handleGiveMemberAccessClick = () => {
		if (!center) {
			return;
		}
		mutateUpdateUser(
			{ id: user.id, centerId: center.id, access: "MEMBER" },
			{
				onError: () => {
					tryLater();
				},
			},
		);
	};
	const handleActivateClick = () => {
		mutateUpdateUser(
			{ id: user.id, activatedAt: new Date() },
			{
				onError: () => {
					tryLater();
				},
			},
		);
	};
	const handleDesactivateClick = () => {
		mutateUpdateUser(
			{ id: user.id, activatedAt: null },
			{
				onError: () => {
					tryLater();
				},
			},
		);
	};
	const handleDeleteUserClick = () => {
		mutateDeleteUser(
			{ id: user.id },
			{
				onError: () => {
					tryLater();
				},
			},
		);
	};
	const handleDeleteUserFromCenterClick = () => {
		if (!center) {
			return;
		}
		mutateUnlinkUser(
			{ id: center.id, userId: user.id },
			{
				onError: () => {
					tryLater();
				},
			},
		);
	};
	const updateUserRole = useDisclosure();
	const handleUpdateRoleClick = () => {
		updateUserRole.onOpen();
	};
	const { onOpen, onClose, isOpen } = useDisclosure();

	return (
		<>
			<Menu {...props}>
				<MenuButton as={Button} variant="outlined" transition="all 0.2s">
					<MoreIcon w="24px" h="24px" />
				</MenuButton>
				<MenuList>
					{user.access === "ADMIN" && (
						<MenuItem lineHeight={"24px"} onClick={handleGiveMemberAccessClick}>
							Donner les accès membre
						</MenuItem>
					)}
					{user.access === "MEMBER" && (
						<MenuItem lineHeight={"24px"} onClick={handleGiveAdminAccessClick}>
							Donner les accès admin au centre
						</MenuItem>
					)}
					<MenuItem lineHeight={"24px"} onClick={handleUpdateRoleClick}>
						Modifier le rôle de l'utilisateur
					</MenuItem>
					<SimpleModal
						isOpen={updateUserRole.isOpen}
						onClose={updateUserRole.onClose}
						title="Modifier le rôle de l'utilisateur"
						hideButtons
					>
						<UserCenterForm
							user={user}
							center={center}
							onSuccess={() => {
								updateUserRole.onClose();
								queryClient.invalidateQueries("center-users-paginated");
							}}
						/>
					</SimpleModal>
					{user.activatedAt === null && (
						<MenuItem lineHeight={"24px"} onClick={handleActivateClick}>
							Activer le compte
						</MenuItem>
					)}
					<MenuItem lineHeight={"24px"} onClick={() => onOpen()}>
						Modifier les informations de l'utilisateur
					</MenuItem>
					<MenuItem
						lineHeight={"24px"}
						onClick={handleDeleteUserClick}
						color="red.500"
					>
						Supprimer l'utilisateur
					</MenuItem>
					<MenuItem
						lineHeight={"24px"}
						onClick={handleDeleteUserFromCenterClick}
						color="red.500"
					>
						Supprimer du centre
					</MenuItem>
					{user.activatedAt !== null && (
						<MenuItem
							lineHeight={"24px"}
							onClick={handleDesactivateClick}
							color="red.500"
						>
							Désactiver le compte
						</MenuItem>
					)}
				</MenuList>
			</Menu>
			{/* Update user information modal */}
			<Modal isOpen={isOpen} onClose={onClose} isCentered size={"xl"}>
				<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
				<ModalContent>
					<ModalHeader>Modifier les informations de l'utilisateur</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<UserForm
							user={user}
							onSuccess={() => {
								queryClient.invalidateQueries("users");
								onClose();
							}}
						></UserForm>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

const ContactCentersUsersTableActions = ({ center }: { center: Center }) => {
	const { onOpen, isOpen, onClose } = useDisclosure();
	const ref = useRef<InviteUserFormHandler>(null);
	const { mutate: mutateSendInvitations } = useMutation(sendInvitations);
	const handleSubmit = () => {
		if (ref.current && center) {
			const values = ref.current.values();
			mutateSendInvitations(
				{ center, users: values },
				{
					onSuccess: () => {
						onClose();
					},
				},
			);
		}
	};
	return (
		<>
			<PrimaryButton w={"fit-content"} onClick={onOpen}>
				<AddIcon mr={4}></AddIcon>Ajouter un utilisateur
			</PrimaryButton>
			<Modal isOpen={isOpen} onClose={onClose} isCentered>
				<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
				<ModalContent maxW={"none"} w="1000px">
					<ModalHeader>
						<Flex alignItems={"center"}>
							<Flex
								w="48px"
								h="48px"
								bg="zinc.50"
								border="solid 1px"
								borderColor="brand.100"
								justifyContent="center"
								alignItems="center"
								borderRadius={100}
								mr="8px"
							>
								<AttachedFileIcon
									w="24px"
									h="24px"
									color="brand.700"
								></AttachedFileIcon>
							</Flex>
							<Text lineHeight={"48px"}>Ajouter un utilisateur</Text>
						</Flex>
					</ModalHeader>
					<ModalCloseButton onClick={onClose} />

					<ModalBody>
						<LinkUserForm ref={ref}></LinkUserForm>
					</ModalBody>

					<ModalFooter borderTop="solid 1px #E2E8F0">
						<Flex direction={"column"} w="100%" alignItems={"center"} gap={4}>
							<PrimaryButton onClick={handleSubmit}>Ajouter</PrimaryButton>
							<Button variant="link" color="brand.700" onClick={onClose} mb={4}>
								Plus tard
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

const ContactCentersUsersTableToolbar = ({
	center,
}: {
	center?: Center | null;
}) => {
	return (
		<Flex my="1rem" justifyContent="space-between">
			<SearchBar />
			{center && <ContactCentersUsersTableActions center={center} />}
		</Flex>
	);
};

const ContactCentersUsersTable = ({
	id,
}: {
	id: number | string | undefined;
}) => {
	const { data: center } = useCenter(id);

	const columns = useMemo<ColumnDef<User>[]>(
		() => [
			{
				accessorFn: (row) => (
					<Flex textTransform="capitalize" alignItems="center" h="44px">
						<Avatar
							name={`${row.firstName} ${row.lastName}`}
							h="40px"
							w="40px"
							mr="20px"
						></Avatar>
						{row.firstName} {row.lastName}
					</Flex>
				),
				id: "full_name",
				header: "Utilisateur",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.email,
				id: "email",
				header: "Email",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Box textTransform="capitalize">{i18n.t(row.role)}</Box>
				),
				id: "role",
				header: "Rôle",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Box textTransform="capitalize">{i18n.t(row.access)}</Box>
				),
				id: "access",
				header: "Accès",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Box textTransform="capitalize">
						{row.activatedAt === null ? "Pas activé" : "Activé"}
					</Box>
				),
				id: "state",
				header: "Status",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <MoreMenu center={center} user={row} />,
				id: "more",
				header: "",
				cell: (info) => info.getValue(),
			},
		],
		[center],
	);
	return (
		<DataTable
			columns={columns}
			paginatedQuery={useCenterUsersPaginated({ id })}
			w={["20rem", "auto", "200px", "140px", "140px", "40px"]}
			flexGrow={[0, 1, 0, 0, 0, 0]}
			toolbar={<ContactCentersUsersTableToolbar center={center} />}
			cellLink={(cell: Cell) => {
				if (cell.id.endsWith("more")) {
					return;
				}
				return `/contacts/users/${cell.row.id}/`;
			}}
		></DataTable>
	);
};

export default ContactCentersUsersTable;
